<template>
  <v-select
    :options="selectOptions"
    class="form-live-search"
    label="displayTitle"
    :filterable="false"
    @input="onInput"
    @search="onItemSearch"
    :selectable="option => option.selectable"
    :clearable="clearable"
    :value="value"
    :loading="loading"
  >
    <template #no-options="{ search, searching }">
      <template v-if="searching">
        {{ $t('liveSearch.noResults') }} <em>{{ search }}</em
        >.
      </template>
    </template>
    <template #option="{ displayTitle, displaySubtitle, objType }">
      <hr class="option division" v-if="objType === 'division'" />
      <div :class="`option ${objType}`" :title="`${displayTitle} ${displaySubtitle}`" v-else>
        {{ formattedTitle(displayTitle, displaySubtitle) }}<span>{{ displaySubtitle }}</span>
      </div>
    </template>
  </v-select>
</template>

<script>
import debounce from 'lodash/debounce'

export default {
  name: 'LiveSearch',
  props: {
    onSelected: { type: Function, required: true },
    onSearch: { type: Function, required: true },
    itemsWithSections: { type: Object, default: () => ({}) },
    items: { type: Array, default: () => [] },
    clearable: { type: Boolean, default: true },
    selectedValue: { type: Object, default: null, required: false },
    loading: { type: Boolean, default: false },
  },
  data: () => ({
    value: null,
  }),
  computed: {
    optionsFromObject() {
      if (Object.keys(this.itemsWithSections).length === 0) return []

      const { ['null']: regularItems, ...sectionItems } = this.itemsWithSections
      const sectionOptions = Object.entries(sectionItems).flatMap(([section, items]) =>
        items.length == 0
          ? []
          : [
              // A section in the select has a title
              this.itemToOption(section, 'title'),
              // Then the options
              ...items.map(item => this.itemToOption(item, 'option')),
              // And finally a divider
              this.itemToOption(section, 'division'),
            ],
      )
      const regularOptions = regularItems.map(item => this.itemToOption(item, 'option'))
      return [...sectionOptions, ...regularOptions]
    },
    optionsFromList() {
      return this.items.map(r => this.itemToOption(r, 'option'))
    },
    selectOptions() {
      return Object.keys(this.itemsWithSections).length > 0 ? this.optionsFromObject : this.optionsFromList
    },
  },
  methods: {
    itemToOption(object, objType) {
      return {
        objType: objType,
        displayTitle: object.formattedTitle || object,
        displaySubtitle: object.formattedSubtitle || '',
        value: object,
        selectable: !(objType == 'title' || objType == 'division'),
      }
    },
    formattedTitle(title, subtitle) {
      return title.length > 40 && subtitle.length > 0 ? `${title.substring(0, 40)}...` : title
    },
    onItemSearch(searchText, loading) {
      loading(true)
      this.debouncedSearch(searchText, loading)
    },
    debouncedSearch: debounce(function (text, loading) {
      this.onSearch(text)
      loading(false)
    }, 350),
    onInput(selectedValue) {
      if (selectedValue) {
        this.value = selectedValue
        this.onSelected(selectedValue)
      }
    },
  },
  watch: {
    selectedValue: {
      handler() {
        this.value = this.selectedValue
      },
      immediate: true,
    },
  },
}
</script>

<style lang="scss">
hr {
  margin: 0.5rem 0;
  border-style: dashed;
}

.option {
  overflow: hidden;
  text-overflow: ellipsis;

  span {
    color: rgba(60, 60, 60, 0.5);
  }

  &.title {
    color: #444;
    font-weight: 700;
  }

  &.division {
    border-color: #444;
  }
}
</style>
